import React, { useState } from 'react';
import { Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorDisplay, LoadingSpinner } from '../../components/common';
import { fetchListingById, updateListing } from '../../api/services';
import { useAuthentication, useNotifications } from '../../store/AppContext';
import { useFetch, useUserProfile } from '../../hooks';
import { ListingForm, useListingForm, validateListingFormData } from '../../features/account/listingForm';
import {
  createListingFormStateFromListing,
  createListingPayload,
  deriveRoutePlannerPointsFromGeoJson,
  isSupportedGeoJson,
  parseGeoJsonFile,
} from '../../features/account/listingEditor';
import { AccountLayout } from '../../features/account/shell';

export function EditListingPage() {
  const navigate = useNavigate();
  const { listingId } = useParams<{ listingId: string }>();
  const { currentUser } = useAuthentication();
  const { showNotification } = useNotifications();
  const { data: profile, isLoading: isProfileLoading } = useUserProfile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isPilot = currentUser?.membership?.USER_TYPE === 'PILOT_B2B_COMPANY';

  const {
    data: listingResponse,
    isLoading: isListingLoading,
    error: listingError,
    refetch: refetchListing,
  } = useFetch(
    () => fetchListingById(listingId!),
    [listingId],
    { enabled: !!listingId }
  );

  const {
    formData,
    setFormData,
    handleFieldChange,
    handleDescriptionChange,
    handleSelectChange,
    handleLocationChange,
    handleDateChange,
    handlePermissionChange,
    handleLocationSelect,
  } = useListingForm();

  React.useEffect(() => {
    if (listingResponse?.data) {
      setFormData(createListingFormStateFromListing(listingResponse.data));
    }
  }, [listingResponse, setFormData]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.geojson') && !file.name.endsWith('.json')) {
      showNotification('Nieprawidłowy format pliku. Wybierz plik .geojson lub .json', 'error');
      return;
    }

    try {
      const parsedJson = await parseGeoJsonFile(file);
      if (!isSupportedGeoJson(parsedJson)) {
        showNotification('Nieprawidłowy format GeoJSON.', 'error');
        return;
      }

      setFormData((prev) => ({
        ...prev,
        route_geojson: parsedJson,
        route_points: deriveRoutePlannerPointsFromGeoJson(parsedJson),
      }));
      showNotification('Plik GeoJSON został wczytany pomyślnie.', 'success');
    } catch {
      showNotification('Błąd podczas odczytu pliku JSON.', 'error');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentUser || !profile || !listingId) return;

    const validationError = validateListingFormData(formData, { enforceFutureDates: false });
    if (validationError) {
      showNotification(validationError, 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = createListingPayload(formData);
      await updateListing(listingId, payload);
      showNotification('Ogłoszenie zostało zaktualizowane!', 'success');
      navigate('/account/my-listings');
    } catch (err: any) {
      showNotification(err.message || 'Wystąpił błąd podczas aktualizacji ogłoszenia.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isProfileLoading || isListingLoading) {
    return (
      <AccountLayout pageTitle="Edytuj ogłoszenie">
        <LoadingSpinner message="Ładowanie danych..." />
      </AccountLayout>
    );
  }

  if (listingError) {
    return (
      <AccountLayout pageTitle="Edytuj ogłoszenie">
        <ErrorDisplay message={listingError} onRetry={refetchListing} />
      </AccountLayout>
    );
  }

  if (isPilot) {
    return (
      <AccountLayout pageTitle="Edytuj ogłoszenie">
        <Alert severity="error">
          Piloci nie mogą edytować ogłoszeń. Ta funkcja jest dostępna tylko dla firm transportowych.
        </Alert>
      </AccountLayout>
    );
  }

  const isOwner = listingResponse?.data?.owner?.documentId === currentUser?.membership?.company_transport?.documentId;
  if (!isOwner && listingResponse?.data) {
    return (
      <AccountLayout pageTitle="Edytuj ogłoszenie">
        <Alert severity="error">Nie masz uprawnień do edycji tego ogłoszenia.</Alert>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout pageTitle="Edytuj ogłoszenie">
      <ListingForm
        formData={formData}
        setFormData={setFormData}
        handleFieldChange={handleFieldChange}
        handleDescriptionChange={handleDescriptionChange}
        handleSelectChange={handleSelectChange}
        handleLocationChange={handleLocationChange}
        handleDateChange={handleDateChange}
        handlePermissionChange={handlePermissionChange}
        handleLocationSelect={handleLocationSelect}
        handleFileUpload={handleFileUpload}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitLabel="Zapisz zmiany"
        isEdit
      />
    </AccountLayout>
  );
}
